import { sharedLib } from "@shared/services/shared.service";
import { useEffect, useState } from "react";
import {
  DisplayResultMessage,
  ExternalAccountType,
  GroupData,
  OpenMessage,
  SubGroupOverview,
  LanguageNameSpaces,
} from "@shared/types";
import { useTheme } from "styled-components";
import {
  ConnectGroupButtonData,
  JoinGroupButtonType,
} from "@shared/features/newMarketplace/types";
import { useUser } from "@shared/utils/hooks/misc";
import { getIsHighRisk } from "@shared/features/newMarketplace/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useSubGroupInfo = (
  subGroupOverview: SubGroupOverview,
  userGroupData: GroupData | undefined,
  displayResultMessage: DisplayResultMessage,
  openMessage: OpenMessage,
  sendAnalyticsEvent?: () => void,
  openConnectExternalAccountPage?: (
    externalAccountType: ExternalAccountType
  ) => void
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { user } = useUser();
  const appTheme = useTheme();
  const [externalAccountType, setExternalAccountType] = useState<
    ExternalAccountType | undefined
  >();
  const [externalAccountModalVisible, setExternalAccountModalVisible] =
    useState(false);
  const [connectGroupModalVisible, setConnectGroupModalVisible] =
    useState(false);
  const [localGroupData, setLocalGroupData] = useState<GroupData>();
  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);

  const isHighRisk = getIsHighRisk(
    subGroupOverview?.backtesting_score_breakdown
  );

  useEffect(() => {
    if (!localGroupData && userGroupData) {
      setLocalGroupData(userGroupData);
    }
  }, [localGroupData, userGroupData]);

  const [
    subscribeToGroupFreeTrial,
    { isLoading: subscribeToGroupFreeTrialLoading },
  ] = sharedLib.sharedApi.configuration.useSubscribeToGroupFreeTrialMutation();

  const [subscribeToFreeGroup, { isLoading: subscribeToFreeGroupLoading }] =
    sharedLib.sharedApi.configuration.useSubscribeToGroupFreeSubscriptionMutation();

  let groupLink = subGroupOverview.join_group_link || undefined;
  const noExternalAccount =
    !user?.is_telegram_connected && groupLink?.includes("t.me/");
  const noExchangeConnected = !user?.has_valid_clients_connected;

  useEffect(() => {
    if (!connectGroupModalVisible && userGroupData) {
      updateGroupData();
    }
  }, [connectGroupModalVisible, userGroupData]);

  const handleContinue = (endpoint: any, successMessage?: string) => {
    return displayResultMessage(
      endpoint({ sub_group_id: subGroupOverview.group_id }).unwrap(),
      successMessage
    );
  };

  const onJoinGroupClick = () => {
    sendAnalyticsEvent?.();
    if (noExternalAccount) {
      openConnectExternalAccountPage?.(ExternalAccountType.TELEGRAM);
      setExternalAccountType(ExternalAccountType.TELEGRAM);
    } else if (groupLink) {
      setConnectGroupModalVisible(true);
    }
  };

  const openConnectGroupModal = () => setConnectGroupModalVisible(true);

  const updateGroupData = () => setLocalGroupData(userGroupData);

  const getButtonData = (): ConnectGroupButtonData | undefined => {
    if (localGroupData) {
      if (localGroupData?.has_active_subscription) {
        if (localGroupData?.is_free || !localGroupData?.free_trial_ended) {
          return {
            buttonText: t(JoinGroupButtonType.ACTIVE_FREE_TRIAL),
            onClick: () => undefined,
            disabled: true,
          };
        } else {
          return {
            buttonText: t(JoinGroupButtonType.JOINED),
            disabled: true,
            onClick: () =>
              openMessage(t("group_info.already_member"), "warning"),
          };
        }
      } else if (localGroupData?.is_free)
        return {
          buttonText: t(JoinGroupButtonType.SUBSCRIBE),
          onClick: openConnectGroupModal,
          onContinue: () => handleContinue(subscribeToFreeGroup),
          continueSuccessMessage: t("group_info.subscribed_to_group", {
            groupName: localGroupData.name,
          }),
          onClose: updateGroupData,
          loading: subscribeToFreeGroupLoading,
          modalTitle: t("group_info.modals.subscribe_free_group_title"),
          modalContent: t("group_info.modals.subscript_free_group_content", {
            groupName: localGroupData?.name,
          }),
        };
      else if (
        localGroupData?.has_free_trial &&
        !localGroupData?.free_trial_ended
      )
        return {
          buttonText: t(JoinGroupButtonType.START_FREE_TRIAL),
          onClick: openConnectGroupModal,
          onContinue: () => handleContinue(subscribeToGroupFreeTrial),
          continueSuccessMessage: t("group_info.free_trial_started", {
            groupName: localGroupData.name,
          }),
          onClose: updateGroupData,
          loading: subscribeToGroupFreeTrialLoading,
          modalTitle: t("group_info.modals.start_free_trial_title"),
          modalContent: t("group_info.modals.start_free_trial_content", {
            groupName: localGroupData?.name,
          }),
        };
      return {
        buttonText: t(JoinGroupButtonType.JOIN_GROUP),
        onClick: onJoinGroupClick,
        disabled: !groupLink && !noExternalAccount,
        link: groupLink,
        addIcon: true,
        modalTitle: t("group_info.modals.join_group_title"),
      };
    }
  };

  const onCancel = () => {
    setExternalAccountType(undefined);
    setExternalAccountModalVisible(false);
  };

  let buttonData = getButtonData();
  if (!!buttonData && noExchangeConnected) {
    buttonData = {
      ...buttonData,
      disabled: true,
      onClick: () => setNoExchangeModalVisible(true),
    };
  }

  return {
    user,
    appTheme,
    externalAccountType,
    externalAccountModalVisible,
    connectGroupModalVisible,
    setConnectGroupModalVisible,
    groupLink,
    buttonData,
    onCancel,
    onJoinGroupClick,
    setExternalAccountType,
    setExternalAccountModalVisible,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    isHighRisk,
  };
};

export default useSubGroupInfo;
